import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, throwError, of } from 'rxjs';
// tslint:disable-next-line:max-line-length
import { ExchangeCoinWithdrawal, ExchangeCoinDeposit, ExchangeTransactionAgreement, ExchangeTransactionAgreementType, ExchangeCoinDepositAddress, ExchangeCoinWithdrawalCurrencies, ExchangeCoinWithdrawalCurrencyInfo } from '../models/exchange-transaction';
import { switchMap, map } from 'rxjs/operators';
import _each from 'lodash-es/each';
import { CryptocurrencyAddress } from '../models/cryptocurrency-address';
import { Coin } from '../models/coin';
import { Pagination } from '../models/pagination';
import { SystemPart } from '../models/system-part';
import { OtpOption, SendOtpResponse } from '../models/otp-option';

@Injectable({
  providedIn: 'root'
})
export class ExchangeTransactionService {

  withdrawlQuery = `
    id
    amount{amount, currency}
    amountFee{amount, currency}
    amountWithdrawn{amount, currency}
    dateCreated
    dateDone
    dateCanceled
    status
    statusName
    txHash
    avgExchangeRate{
      amount
      pair
    }
    receiverAddress{
      address
      currency
      additionalId
      legacyAddress
    }
    otpConfirmed
    emailConfirmed
    cancelable
    origin
  `;

  depositQuery = `
    id
    amount{amount currency}
    dateCreated
    dateDone
    dateCanceled
    status
    statusName
    txHash
    avgExchangeRate{amount pair}
    receiverAddress{
      address
      currency
      additionalId
      legacyAddress
    }
    origin
  `;

  constructor(private api: ApiService) { }

  getPendingAgreements(): Observable<ExchangeTransactionAgreement[]> {
    return this.api.get(`{
      exchangeCoinDepositCurrencies{
        pendingAgreements{
          type
          title
          description
          link
        }
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['exchangeCoinDepositCurrencies']['pendingAgreements']);
      }),
      this.api.unifyErrors()
    );
  }

  getWithdrawalCurrencies(currencies: string[] = null): Observable<ExchangeCoinWithdrawalCurrencies> {
    let params = '';
    if (currencies != null) {
      params = `(currencies: ["${currencies.join('","')}"])`;
    }

    return this.api.get(`{
      exchangeCoinWithdrawalCurrencies${params}{
        currencies{
          code
          fee{amount currency}
          minAmount{amount currency}
          network {name code}
          info{
            severity
            text
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['exchangeCoinWithdrawalCurrencies']);
      }),
      this.api.unifyErrors()
    );
  }

  getWithdrawalCurrency(currency: string): Observable<ExchangeCoinWithdrawalCurrencyInfo> {
    return this.getWithdrawalCurrencies([currency]).pipe(
      map((response) => {
        return response.currencies[0];
      })
    );
  }


  getDepositAddress(currency: string): Observable<ExchangeCoinDepositAddress> {
    return this.api.get(`{
      exchangeCoinDepositAddress(currency: "${currency}"){
        address{
          address
          currency
          additionalId
          legacyAddress
          newFormatAddress
        }
        network {
          name
          code
        }
        info{
          severity
          text
        }
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['exchangeCoinDepositAddress']);
      }),
      this.api.unifyErrors()
    );
  }

  agreeOnDepositAgreements(agreementTypes: ExchangeTransactionAgreementType[]): Observable<boolean> {
    let query = '';

    agreementTypes.forEach(agreementType => {
      query += `
        ${agreementType}: agreeExchangeCoinDepositAgreement(type: ${agreementType}){
          ... on GeneralResponse{
            success
          }
          ... on OperationError{
            errors{
              message
              field
            }
          }
        }
      `;
    });

    return this.api.get(`mutation{
      ${query}
    }`).pipe(
      switchMap(response => {
        const data = response['data'];
        let success = true;

        _each(data, result => {
          if (result['errors']) {
            return throwError(result);
          } else {
            success = success && result['success'];
          }
        });

        return of(success);
      }),
      this.api.unifyErrors()
    );
  }

  getWithdrawal(id: string): Observable<ExchangeCoinWithdrawal> {
    return this.api.get(`{
      exchangeCoinWithdrawal(id: ${id}){
        ${this.withdrawlQuery}
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['exchangeCoinWithdrawal']);
      }),
      this.api.unifyErrors()
    );
  }

  getWithdrawals(first = 10, after: string = null, currency: string = null): Observable<Pagination<ExchangeCoinWithdrawal>> {
    return this.api.get(`{
      exchangeCoinWithdrawals(
        first: ${first}
        ${after ? ', after: "' + after + '"' : ''}
        ${currency ? ', currency: "' + currency + '"' : ''}
      ){
        edges{
          node{
            ${this.withdrawlQuery}
          }
          cursor
        }
        pageInfo{
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['exchangeCoinWithdrawals']);
      }),
      this.api.unifyErrors()
    );
  }

  getDeposit(id: string): Observable<ExchangeCoinDeposit> {
    return this.api.get(`{
      exchangeCoinDeposit(id: ${id}){
        ${this.depositQuery}
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['exchangeCoinDeposit']);
      }),
      this.api.unifyErrors()
    );
  }

  getDeposits(first = 10, after: string = null, currency: string = null): Observable<Pagination<ExchangeCoinWithdrawal>> {
    return this.api.get(`{
      exchangeCoinDeposits(
        first: ${first}
        ${after ? ', after: "' + after + '"' : ''}
        ${currency ? ', currency: "' + currency + '"' : ''}
      ){
        edges{
          node{
            ${this.depositQuery}
          }
          cursor
        }
        pageInfo{
          startCursor
          endCursor
          hasNextPage
        }
        totalCount
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['exchangeCoinDeposits']);
      }),
      this.api.unifyErrors()
    );
  }

  createWithdrawal(address: CryptocurrencyAddress, amount: Coin, origin = SystemPart.EXCHANGE): Observable<ExchangeCoinWithdrawal> {
    return this.api.get(`mutation{
      createExchangeCoinWithdrawal(
        address: {
          address: "${address.address}",
          currency: "${address.currency}"
          ${address.additionalId ? `, additionalId: "${address.additionalId}"` : ''}
        },
        amount: {
          amount: "${amount.amount}",
          currency: "${amount.currency}"
        },
        origin: ${origin}
      ){
        ... on ExchangeCoinWithdrawal{
          ${this.withdrawlQuery}
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['createExchangeCoinWithdrawal'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  approveWithdrawalByEmail(id: string, code: string): Observable<ExchangeCoinWithdrawal> {
    return this.api.get(`mutation{
      approveExchangeCoinWithdrawalByEmail(
        id: ${id},
        code: "${code}"
      ){
        ... on ExchangeCoinWithdrawal{
          ${this.withdrawlQuery}
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['approveExchangeCoinWithdrawalByEmail'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  resendWithdrawalEmailOtp(id: string): Observable<boolean> {
    return this.api.get(`mutation{
      resendExchangeCoinWithdrawalEmailOtp(id: ${id}){
        ... on GeneralResponse{
          success
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['resendExchangeCoinWithdrawalEmailOtp'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data['success']);
      }),
      this.api.unifyErrors()
    );
  }

  getWithdrawalOtpOptions(): Observable<OtpOption[]> {
    return this.api.get(`{
      exchangeCoinWithdrawalOtpOptions{
        otpOptions
      }
    }`).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['exchangeCoinWithdrawalOtpOptions']['otpOptions']);
      }),
      this.api.unifyErrors()
    );
  }

  sendWithdrawalOtp(id: string, otpOption: OtpOption): Observable<SendOtpResponse> {
    return this.api.get(`mutation sendExchangeCoinWithdrawalOtp($id: Int!, $otpOption: OtpOption!){
      sendExchangeCoinWithdrawalOtp(id: $id, otpOption: $otpOption){
        ... on SendOtpResponse{
          success
          maskedPhoneNumber
          option
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      id,
      otpOption
    }).pipe(
      switchMap(response => {
        const data = response['data']['sendExchangeCoinWithdrawalOtp'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  approveWithdrawalOtp(id: string, code: string): Observable<ExchangeCoinWithdrawal> {
    return this.api.get(`mutation approveExchangeCoinWithdrawalOtp($id: Int!, $code: String!){
      approveExchangeCoinWithdrawalOtp(id: $id, code: $code){
        ... on ExchangeCoinWithdrawal{
          ${this.withdrawlQuery}
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`, {
      id,
      code
    }).pipe(
      switchMap(response => {
        const data = response['data']['approveExchangeCoinWithdrawalOtp'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  cancelWithdrawal(id: string): Observable<ExchangeCoinWithdrawal> {
    return this.api.get(`mutation{
      cancelExchangeCoinWithdrawal(id: ${id}){
        ... on ExchangeCoinWithdrawal{
          ${this.withdrawlQuery}
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['cancelExchangeCoinWithdrawal'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }

  cancelDeposit(id: string): Observable<ExchangeCoinDeposit> {
    return this.api.get(`mutation{
      cancelExchangeCoinDeposit(id: ${id}){
        ... on ExchangeCoinDeposit{
          ${this.depositQuery}
        }
        ... on OperationError{
          errors{
            message
            field
          }
        }
      }
    }`).pipe(
      switchMap(response => {
        const data = response['data']['cancelExchangeCoinDeposit'];

        if (data['errors']) {
          return throwError(data);
        }

        return of(data);
      }),
      this.api.unifyErrors()
    );
  }
}
